import React, { ReactElement, ReactNode, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { apiClient } from '@api/client';
import { FigmaOAuthCompleteErrorDto } from '@api/Api';
import { CheckCircleIcon, ExpiredIcon, LogoIcon, WarningIcon } from '@icons';
import '@components/ExpiredLink/ExpiredLink.less';

type FailureCode = Exclude<
  FigmaOAuthCompleteErrorDto['errorCode'],
  'account_mismatch'
>;

const FAILURES: Record<FailureCode, { title: string; text: string }> = {
  expired: {
    title: 'This link has expired',
    text: 'For security, Figma connection links work once and for 10 minutes. Start connecting again, from the plugin or the Integration Hub.'
  },
  denied: {
    title: 'Figma access wasn’t allowed',
    text: 'StreamWork needs this access to link exports to their Figma file and sync their comments. Try again when you’re ready.'
  },
  not_member: {
    title: 'You’re not in this organization',
    text: 'Your StreamWork account isn’t a member of the organization you picked. Go back to Figma and choose another one.'
  },
  not_installed: {
    title: 'Figma isn’t set up for this organization',
    text: 'An admin sets Figma up in StreamWork, in Settings › Integrations. Then connect your Figma account from the plugin again.'
  },
  plan_not_allowed: {
    title: 'Figma isn’t in your plan',
    text: 'Your organization’s StreamWork plan doesn’t include the Figma integration. An admin can upgrade it in Settings › Billing.'
  },
  admin_required: {
    title: 'Only admins can change Figma’s settings',
    text: 'Your role in this organization changed. Ask an admin to connect the posting account in Settings › Integrations › Figma.'
  },
  failed: {
    title: 'Couldn’t connect Figma',
    text: 'Something went wrong while talking to Figma, or the integration was removed meanwhile. Try connecting again.'
  }
};

function Layout({
  icon,
  title,
  children
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="expired_link_container">
      <span className="expired_link_container__logotype">
        <LogoIcon className="logo" />
      </span>
      <div className="expired_link_container__body">
        {icon}
        <div className="title">{title}</div>
        <div className="description">{children}</div>
      </div>
      <div className="expired_link_container__footer" style={{ height: 36 }} />
    </div>
  );
}

function errorOf(err: unknown): FigmaOAuthCompleteErrorDto | null {
  if (!axios.isAxiosError(err)) return null;
  const data = err.response?.data as FigmaOAuthCompleteErrorDto | undefined;
  return typeof data?.errorCode === 'string' ? data : null;
}

/** Sends what Figma redirected with to the backend and shows the outcome. */
function FigmaOAuthResult({
  code,
  state,
  error,
  loader
}: {
  code: string;
  state: string;
  error: string;
  loader: ReactElement;
}) {
  const complete = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.figma.figmaControllerOauthComplete({
        code: code || undefined,
        state: state || undefined,
        error: error || undefined
      });
      return data;
    }
  });
  // Once: the state works only once, so a second call would read "expired".
  const started = useRef(false);
  const { mutate } = complete;
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    mutate();
  }, [mutate]);

  if (complete.isSuccess && complete.data.purpose === 'posting') {
    return (
      <Layout
        icon={<CheckCircleIcon style={{ color: '#58c97a' }} />}
        title="Posting account connected"
      >
        StreamWork now posts comments to Figma as{' '}
        <b>{complete.data.figmaHandle}</b> for{' '}
        <b>{complete.data.organizationName}</b> when neither the commenter nor
        the uploader has a connected Figma account. You can close this tab.
      </Layout>
    );
  }
  if (complete.isSuccess) {
    return (
      <Layout
        icon={<CheckCircleIcon style={{ color: '#58c97a' }} />}
        title="Figma connected"
      >
        Your Figma account <b>{complete.data.figmaHandle}</b> is now connected
        to <b>{complete.data.organizationName}</b>. Go back to Figma to
        continue. You can close this tab.
      </Layout>
    );
  }
  if (!complete.isError) return loader;

  const failure = errorOf(complete.error);
  if (failure?.errorCode === 'account_mismatch') {
    // Display names aren't unique, so the email tells the accounts apart.
    const sameName = failure.expectedName === failure.figmaHandle;
    return (
      <Layout icon={<WarningIcon />} title="That’s a different Figma account">
        You allowed access as <b>{failure.figmaHandle}</b>
        {failure.figmaEmail ? ` (${failure.figmaEmail})` : ''}, but the plugin
        runs in Figma{' '}
        {failure.expectedName ? (
          <>
            signed in as <b>{failure.expectedName}</b>
            {sameName ? ', a different account with the same name' : ''}
          </>
        ) : (
          'signed in to another account'
        )}
        . In this browser, switch to the Figma account you use in Figma (or sign
        out of figma.com), then click Connect Figma in the plugin again. Nothing
        was connected.
      </Layout>
    );
  }
  const { title, text } = FAILURES[failure?.errorCode ?? 'failed'];
  return (
    <Layout
      icon={
        failure?.errorCode === 'expired' ? <ExpiredIcon /> : <WarningIcon />
      }
      title={title}
    >
      {text}
    </Layout>
  );
}

/**
 * Figma redirects here after its consent screen, when a user connects Figma
 * from the plugin. Public: the one-time `state` identifies the StreamWork
 * user, and the backend checks that the Figma account that approved is the
 * one signed in to Figma where the plugin runs.
 */
export default function FigmaOAuth({ loader }: { loader: ReactElement }) {
  const [searchParams] = useSearchParams();
  if (['code', 'state', 'error'].some((name) => searchParams.has(name))) {
    // Auth0 reads `code`, `state` and `error` as its own callback: rename
    // what Figma sent, like the Asana page does.
    ['code', 'state', 'error'].forEach((name) => {
      const value = searchParams.get(name);
      searchParams.delete(name);
      if (value !== null) searchParams.set(`_${name}`, value);
    });
    window.location.search = searchParams.toString();
    return loader;
  }
  return (
    <FigmaOAuthResult
      code={searchParams.get('_code') ?? ''}
      state={searchParams.get('_state') ?? ''}
      error={searchParams.get('_error') ?? ''}
      loader={loader}
    />
  );
}
